export enum Category {
  MUSIC = 'Music',
  SPORTS = 'Sports',
  COMEDY = 'Comedy',
  THEATER = 'Theater',
}

export interface PracticalInfo {
  ticketsEntry: {
    doorTime: string;
    bagPolicy: string;
    idRequired: boolean;
    ticketScanning: string;
  };
  transport: {
    publicTransport: string;
    parking: string;
    rideshare: string;
  };
  weather: {
    type: 'Outdoor' | 'Indoor';
    forecast: string;
    clothing: string;
  };
  rules: {
    prohibited: string[];
    waterPolicy: string;
    payment: string;
  };
  inside: {
    merchQueues: string;
    foodDrinkPrices: string;
    wifi: string;
  };
  soundView: {
    bestStanding: string;
    soundQuality: string;
  };
}

/** One partner's real floor price for one event — always from today's feed
 *  pull, never carried over. `url` is the exact event page on that partner. */
export interface PartnerPrice {
  source: string; // marketplace display name, e.g. "Ticket Squeeze"
  from: number; // lowest listed price (USD) in that partner's feed today
  url: string;
  listings?: number | null;
  /** True when this specific price includes fees (overrides the marketplace
   *  default — e.g. Ticket Squeeze prices are all-in when read from their
   *  live widget, sticker when they fell back to the daily feed). */
  allIn?: boolean;
  /** A before-fees price under half the event's cheapest fees-included price (the build's
   *  markSuspects, shared/providers.mjs): shown with a note, never the headline. */
  suspect?: boolean;
  /** A broker's stand-in price (shared/providers.mjs markPlaceholders): shown last with a note, never quoted. */
  placeholder?: boolean;
  /** The last day a partner pull still held this offer (YYYY-MM-DD); absent = today's pull. The edge
   *  stamps it from the daily tape (functions/_lib/edge-page.ts). */
  seen?: string;
  /** `seen` is more than FRESH_DAYS old (shared/providers.mjs markStale): shown last, never quoted. */
  stale?: boolean;
  /** What `from` is the floor OF: 2 = the cheapest group of two or more tickets (a widget reading),
   *  1 = single-seat groups only, absent = unknown (a feed minimum, which is often one seat). */
  qty?: 1 | 2;
  /** The cheapest single seat, when it is cheaper than `from` (qty 2 readings). */
  from1?: number;
  /** Client-side only: the partner's OTHER storefront price when it differs
   *  (HelloTickets personalizes US vs international) — shown as a note so a
   *  visitor whose storefront we mis-guessed is never ambushed. */
  altFrom?: number;
  altLabel?: string;
}

/** One event's recorded price history (scripts/tape-summary.mjs). Prices are
 *  the cheapest listing per marketplace per day; tuples keep the row small. */
export interface TapeSummary {
  /** first recorded day */
  since: string;
  /** days with a recorded trusted reading (recorded days, not the calendar span) */
  days: number;
  /** Every price below is a daily floor on ONE comparable basis (scripts/tape-core.mjs): the
   *  cheapest unflagged price of the day, a price before fees times its marketplace's fee wedge.
   *  `est` says which of them came from such an estimate. */
  /** latest cross-marketplace floor: [price, date, marketplace] */
  last: [number, string, string];
  /** lowest floor ever recorded: [price, date, marketplace] */
  low: [number, string, string];
  /** highest floor ever recorded: [price, date] */
  high: [number, string];
  /** median daily floor over the last 30 days (needs 5+ days with a floor) */
  med30: number | null;
  /** lowest floor in the last 30 days: [price, date, marketplace] */
  low30: [number, string, string] | null;
  /** [lowest, highest] floor of the last 30 days */
  range30?: [number, number] | null;
  /** days of the last 30 that have a floor (the median's sample) */
  n30?: number;
  /** we have recorded for the whole 30 days: the first reading is 29 or more days before the build day */
  full30?: boolean;
  /** latest floor vs the recording ~7 days earlier (same number of tickets) */
  wk: { change: number; pct: number; span: number } | null;
  /** true where the number came from a price before fees x its fee wedge: say "est." */
  est?: { last: boolean; low: boolean; high: boolean; low30: boolean; high30: boolean; med30: boolean; wkFrom: boolean };
  /** marketplaces in `rows`, cheapest today first */
  cols: string[];
  /** last 30 days, newest first: [date, price per column or null] — each marketplace's OWN price */
  rows: (string | number | null)[][];
  /** one character per cell of `rows` (row-major, `cols.length` per row): '.' as the marketplace's
   *  rule says, '1' fees included against it, '0' before fees against it, 'x' unconfirmed (that
   *  day's phantom, left out of every statistic). Absent when every cell is '.'. */
  marks?: string;
}

/** Shape stored in catalog.ts — no prices, no generated info. */
export interface RawEvent {
  id: string;
  name: string;
  artist?: string;
  teams?: string[];
  /** SeatGeek events: both clubs' full names (`teams` holds its short ones); the picture service's keys. */
  teamNames?: string[];
  /** A game's two clubs' free logos (Wikimedia Commons), '' for one without: small icons beside the
   *  title only (scripts/pictures/apply.mjs). */
  teamLogos?: string[];
  date: string;
  venue: string;
  city: string;
  country: string;
  category: string;
  /** SeatGeek type we queried: concert, nfl, nba, mlb, nhl, mls, ncaa_football, comedy, theater, classical, family … */
  kind?: string;
  /** performer genre ("Country", "Hip-hop") or the most specific taxonomy ("Classical Opera", "Musical") */
  genre?: string;
  tournament?: string;
  context?: string;
  group?: string;
  description: string;
  imageUrl: string;
  heroImageUrl?: string;
  /** Set when the photo is from Wikimedia Commons: shown under the event hero and on /credits. */
  imageCredit?: { author: string; license: string; licenseUrl?: string; source: string; /** set when the photo is of the venue, not the act */ subject?: string };
  lat: number;
  lng: number;
  venueType: 'Stadium' | 'Arena' | 'Theatre';
  featured?: boolean;
  /** Popularity rank within its type (0 = SeatGeek's most wanted). */
  rank?: number;
  /** How wanted it is, 0–100, comparable across categories and countries (scripts/hubs.mjs). */
  demand?: number;
  /** Venue-local showtime "HH:MM" — disambiguates matinee/evening double shows.
   *  Absent while the time is still TBD (SeatGeek's 03:30 placeholder is not a time). */
  time?: string;
  /** Venue street address and the event's UTC offset (SeatGeek venue data), for the Event schema. */
  place?: { street?: string; region?: string; postal?: string; tz?: string; offset?: string };
  /** Public on-sale start (ISO datetime) for an announced event no partner lists yet
   *  (coming-soon pages, workers/radar). */
  onsale?: string;
  /** The announcement says the on-sale is still to be announced (functions/_lib/edge-page.ts expandDoc). */
  onsaleTba?: boolean;
  /** A coming-soon event's venue or city with no list page yet (functions/_lib/edge-page.ts loadEdgeDoc):
   *  the event page prints that name without a link. */
  noPage?: ('venue' | 'city')[];
  /** Exact SeatGeek event permalink (deep link, not search results). */
  sgUrl?: string;
  /** Exact Ticketmaster event permalink (matched at build time). */
  tmUrl?: string;
  /** Exact HelloTickets event permalink (partner; matched at build time).
   *  Affiliate tracking is appended at the /go/ redirect, never stored here. */
  htUrl?: string;
  /** HelloTickets performance id — powers the live retail-price refresh. */
  htId?: string;
  /** Exact Ticket Squeeze event permalink (affiliate datafeed; matched at build time). */
  tsUrl?: string;
  /** TicketNetwork Impact tracking deep link (long-tail partner; matched at
   *  build time, ONLY on events with no tsUrl — same exchange, higher fees,
   *  so the two must never compete on one board). */
  tnUrl?: string;
  /** PER-SITE floor prices — one entry per partner that quoted this event
   *  today. Feeds the per-marketplace price board and the per-site tape. */
  offers?: PartnerPrice[];
  /** Fee basis of lowestPrice — false means the headline number is a sticker price. */
  lowestAllIn?: boolean;
  /** True when partner offers do not share a fee basis (ranking is not like-for-like). */
  mixedBasis?: boolean;
  /** Cross-site daily floor for the last ≤14 recorded days (from our tape) —
   *  powers the sparkline on event cards with zero extra requests. */
  trend?: number[];
  /** Yesterday→today change of the cross-site floor, only when both days' floors are fees-included
   *  readings for the same number of tickets (scripts/tape-core.mjs dayMove); absent otherwise. */
  dayChange?: number;
  /** The basis dayChange was measured on (always 'all-in' while present). */
  dayChangeBasis?: 'all-in';
  /** Our recorded price history, summarized at build time (scripts/tape-summary.mjs)
   *  so the event page can server-render it. Trusted observations only. */
  tape?: TapeSummary;
  /** Real lowest all-in price, merged at build time from an approved partner
   *  feed (Ticket Squeeze / TicketNetwork / etc.). Null until a feed is wired. */
  lowestPrice?: number | null;
  listingCount?: number | null;
  priceSource?: string; // e.g. "Ticket Squeeze" — who supplied the price
  priceUrl?: string; // deep link to buy at that price (affiliate-wrapped)
  /** When this row's partner prices were read (ISO datetime), if the build stamps rows.
   *  Absent: the event page falls back to the build's nav.json `generatedAt`. */
  checkedAt?: string;
}

export interface Event extends RawEvent {
  practicalInfo: PracticalInfo;
  /** built from a slim index row — fine for cards, not for the comparison board */
  partial?: boolean;
  /** practicalInfo arrived with the row (prerender embed) — no venue-facts fetch needed */
  withFacts?: boolean;
}

export interface Marketplace {
  name: string;
  kind: 'Resale marketplace' | 'Primary + resale';
  searchUrl: (query: string) => string;
}
